import {
  Badge, Button, Card, DataTable, ErrorState, Notice, PageHeader, Row, Stack, useApi, useI18n,
  type ImportResultResponse, type ImportRow,
} from '@daghfal/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { IMPORT_COLUMNS, parseCsv, readText, toImportRows } from '../importCsv';

/**
 * Registering a nursery's children from a spreadsheet (FR-ADM-21, FR-ADM-22).
 *
 * Checked first and imported second: the check is the API's own dry run and names every row's problems, and the
 * import is all or nothing, because a half-imported register is worse than none (nobody can tell which half landed).
 * The import is offered only once every row passes.
 */
export function ImportChildren() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, tn, formatNumber } = useI18n();
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [unknown, setUnknown] = useState<string[]>([]);
  const [readError, setReadError] = useState(false);

  const check = useMutation({ mutationFn: (body: ImportRow[]) => api.children.import(body, false) });
  const commit = useMutation({
    mutationFn: (body: ImportRow[]) => api.children.import(body, true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['children'] }),
  });

  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    check.reset();
    commit.reset();
    setReadError(false);
    if (!file) return;
    try {
      const read = toImportRows(parseCsv(await readText(file)));
      setRows(read.rows);
      setUnknown(read.unknownColumns);
      if (read.rows.length > 0) check.mutate(read.rows);
    } catch {
      setRows(null);
      setReadError(true);
    }
  };

  const result: ImportResultResponse | undefined = commit.data ?? check.data;
  const ready = check.data !== undefined && check.data.invalidRows === 0 && !commit.data;

  return (
    <Stack>
      <PageHeader
        title={t('import.title')}
        lede={t('import.lede')}
        actions={<Link className="dg-button dg-button--ghost" to="/children">{t('action.back')}</Link>}
      />

      <Card title={t('import.file')} hint={t('import.file.hint')}>
        <Stack>
          <p className="dg-card__hint">{t('import.columns')}</p>
          <code dir="ltr" className="dg-text-sm">{IMPORT_COLUMNS.map((column) => column.headers[0]).join(',')}</code>
          <input type="file" accept=".csv,text/csv" aria-label={t('import.file')} onChange={(event) => void choose(event)} />
          {readError && <Notice tone="danger">{t('import.unreadable')}</Notice>}
          {rows !== null && rows.length === 0 && <Notice tone="warning">{t('import.empty')}</Notice>}
          {unknown.length > 0 && (
            <Notice tone="warning">{t('import.unknownColumns', { columns: unknown.join('، ') })}</Notice>
          )}
        </Stack>
      </Card>

      {check.isPending && <Notice tone="info">{t('import.checking')}</Notice>}
      {check.isError && <ErrorState error={check.error} />}
      {commit.isError && <ErrorState error={commit.error} />}

      {result && (
        <Card
          title={commit.data ? t('import.done') : t('import.checked')}
          action={ready && rows && (
            <Button busy={commit.isPending} busyLabel={t('action.saving')} onClick={() => commit.mutate(rows)}>
              {tn('import.commit', result.validRows)}
            </Button>
          )}
        >
          <Stack>
            <Row className="dg-row--wrap">
              <Badge tone="success">{t('import.valid', { count: formatNumber(result.validRows) })}</Badge>
              {result.invalidRows > 0 && (
                <Badge tone="danger">{t('import.invalid', { count: formatNumber(result.invalidRows) })}</Badge>
              )}
            </Row>
            {result.invalidRows > 0 && <Notice tone="warning">{t('import.fixFirst')}</Notice>}
            {commit.data && <Notice tone="success">{tn('import.committed', commit.data.validRows)}</Notice>}
            <DataTable
              caption={t('import.rows')}
              rows={result.rows}
              rowKey={(row) => row.rowNumber}
              columns={[
                { key: 'row', header: t('import.row'), numeric: true, render: (row) => formatNumber(row.rowNumber) },
                { key: 'child', header: t('child.one'), render: (row) => row.childName || '—' },
                {
                  key: 'verdict',
                  header: t('import.verdict'),
                  render: (row) => (
                    <Stack tight>
                      {row.errors.length === 0 && <Badge tone="success">✓</Badge>}
                      {row.errors.map((error) => <span key={error} className="dg-field__error">{error}</span>)}
                      {row.warnings.map((warning) => <span key={warning} className="dg-card__hint">{warning}</span>)}
                    </Stack>
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
